/**
 * A client's push notifications: what they cost it — the platform's price, or
 * its own, never under the minimum — its subscribers, and what push took from
 * its one wallet in the last 30 days (`app.modules.pushing`).
 *
 * Prices are per 1,000 notifications a push service accepted, before GST. The
 * owner has not chosen push's price model or its rates yet (docs/32 §12): the
 * platform's price is zero until staff set one, and at zero nothing is charged.
 * The platform's own price is set here too: push has no page of its own in this
 * portal.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing } from 'lucide-react';
import {
  ApiError,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  QueryPending,
  ReadOnly,
  Switch,
  api,
  formatNumber,
  toast,
  useCan,
} from '@connect/web-shared';

interface Pricing {
  model: 'per_delivered';
  price_per_1000: string;
  minimum_per_1000: string;
}

interface PushClient {
  has_push: boolean;
  own_price_per_1000: string | null;
  price_per_1000: string;
  platform: Pricing;
  taken_30_days: string;
  subscribers: number;
}

const keys = {
  client: (id: string) => ['push', 'client', id] as const,
};

export function PushPriceCard({ clientPublicId, clientName }: { clientPublicId: string; clientName: string }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.push.edit');
  const url = `/messaging/push/clients/${clientPublicId}`;
  const push = useQuery({ queryKey: keys.client(clientPublicId), queryFn: () => api.get<PushClient>(url) });
  const failed = (e: unknown) => toast.error('Could not change it', e instanceof ApiError ? e.message : undefined);
  const saveOwn = useMutation({
    mutationFn: (price: string | null) => api.put<PushClient>(url, { price_per_1000: price }),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.client(clientPublicId), data);
      toast.success('Saved');
    },
    onError: failed,
  });
  const savePlatform = useMutation({
    mutationFn: (body: Omit<Pricing, 'model'>) => api.put<Pricing>('/messaging/push/pricing', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['push'] });
      toast.success('Saved for every client on the platform price');
    },
    onError: failed,
  });

  if (push.data === undefined) return <QueryPending query={push} label="Loading push" />;
  const data = push.data;
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <BellRing className="size-4" />
            Push notifications
          </span>
        }
        description={`${clientName}'s push notifications are paid from its wallet: each one is charged when a push service accepts it, before GST.`}
      />
      <CardBody className="space-y-4">
        <dl className="grid grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-xs text-muted">Subscribers</dt>
            <dd>{formatNumber(data.subscribers)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Push took, 30 days</dt>
            <dd className="font-mono tabular">₹{data.taken_30_days}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted">Per 1,000 delivered</dt>
            <dd className="font-mono tabular">₹{data.price_per_1000}</dd>
          </div>
        </dl>
        {!data.has_push ? (
          <p className="text-xs text-muted">No push service yet — add one above. A price set now applies from its first notification.</p>
        ) : null}
        <OwnPrice
          key={data.own_price_per_1000 ?? 'platform'}
          data={data}
          canEdit={canEdit}
          saving={saveOwn.isPending}
          onSave={(price) => saveOwn.mutate(price)}
        />
        {canEdit ? (
          <PlatformPrice key={JSON.stringify(data.platform)} pricing={data.platform} saving={savePlatform.isPending} onSave={(p) => savePlatform.mutate(p)} />
        ) : (
          <p className="text-xs text-muted">
            Platform price ₹{data.platform.price_per_1000} per 1,000, minimum ₹{data.platform.minimum_per_1000}.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function OwnPrice({
  data,
  canEdit,
  saving,
  onSave,
}: {
  data: PushClient;
  canEdit: boolean;
  saving: boolean;
  onSave: (price: string | null) => void;
}) {
  const [own, setOwn] = useState(data.own_price_per_1000 !== null);
  const [price, setPrice] = useState(data.own_price_per_1000 ?? data.platform.price_per_1000);
  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-2 rounded-card border border-line p-3">
        <Switch
          label="Its own price"
          hint={`Off: the platform's ₹${data.platform.price_per_1000} per 1,000. Never under the minimum, ₹${data.platform.minimum_per_1000}.`}
          checked={own}
          onChange={(e) => setOwn(e.target.checked)}
        />
        {own ? (
          <Input
            label="Per 1,000 delivered (₹)"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        ) : null}
        {canEdit ? (
          <Button size="sm" loading={saving} onClick={() => onSave(own ? price.trim() : null)}>
            Save
          </Button>
        ) : null}
      </div>
    </ReadOnly>
  );
}

function PlatformPrice({
  pricing,
  saving,
  onSave,
}: {
  pricing: Pricing;
  saving: boolean;
  onSave: (p: Omit<Pricing, 'model'>) => void;
}) {
  const [price, setPrice] = useState(pricing.price_per_1000);
  const [minimum, setMinimum] = useState(pricing.minimum_per_1000);
  return (
    <div className="space-y-2 rounded-card border border-line p-3">
      <p className="text-sm font-medium text-heading">The platform's price</p>
      <p className="text-xs text-muted">
        Charged per notification delivered, from the wallet. For every client without its own; the minimum binds every
        client's own price too. The price model and its rates are the owner's to decide — zero, the default, charges nothing.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Per 1,000 (₹)" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        <Input label="Minimum per 1,000 (₹)" inputMode="decimal" value={minimum} onChange={(e) => setMinimum(e.target.value)} />
      </div>
      <Button size="sm" variant="secondary" loading={saving} onClick={() => onSave({ price_per_1000: price.trim(), minimum_per_1000: minimum.trim() })}>
        Save the platform price
      </Button>
    </div>
  );
}
